import { useState, useEffect, useRef } from 'react';

// MED-08 — optional `leading` mode fires immediately on the first
// DISTINCT value change, then debounces subsequent updates. Useful for
// search inputs where the first keystroke feels laggy under a
// pure-trailing debounce. Callers that omit `options` keep the
// original trailing-only behaviour (backward compatible).
//
// Previously the leading allowance was spent on the initial mount
// (with the initial value, often `""`), so the FIRST real user
// keystroke was still trailing-debounced. We now skip the mount run
// and only fire the leading edge on the first distinct value change.
export function useDebounce<T>(value: T, delay = 300, options?: { leading?: boolean }): T {
  const [debounced, setDebounced] = useState<T>(value);
  const isMountedRef = useRef(false);
  const prevValueRef = useRef<T>(value);
  const leading = options?.leading;

  useEffect(() => {
    // Skip the initial mount — `debounced` already mirrors `value` via
    // useState's initializer, and firing leading here would burn the
    // leading allowance on a no-op.
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      prevValueRef.current = value;
      return;
    }

    if (leading && prevValueRef.current !== value) {
      setDebounced(value);
      prevValueRef.current = value;
      return;
    }

    const timer = setTimeout(() => {
      setDebounced(value);
      prevValueRef.current = value;
    }, delay);
    return () => clearTimeout(timer);
  }, [value, delay, leading]);

  return debounced;
}
